/**
 * BEPRO Shop - Checkout modul (Shopify-style)
 * Two-step checkout: 1) Delivery info → 2) Card payment
 */

import Cart, { escapeHtml } from './cart';
import { showToast } from './toast';

let monriInstance = null;
let monriCard = null;
let currentOrder = null;
let isPaymentStep = false;
let monri3dsRedirecting = false;
let monri3dsListenerInstalled = false;

/**
 * Definitive 3DS2 redirect handler.
 *
 * Monri Components' default behaviour is to run the 3DS challenge inside a
 * fixed-position modal IFRAME. That iframe NEVER completes when the issuer's ACS
 * page refuses to be embedded (X-Frame-Options/CSP) — which is what trapped real
 * customers on bepro-doo.com on the "Obrađujemo plaćanje..." overlay forever.
 *
 * The card iframe posts a `CLIENT_HANDLE_ACTION_REQUIRED` message (and a result
 * message carrying `actionRequired`) to THIS window the moment the gateway returns
 * an action_required step. Both carry the canonical `redirect_to`/`acs_url`
 * (…/client_redirect — Monri's own "client handles the redirect" URL). We catch
 * that message and drive a FULL-PAGE top-window navigation ourselves, so the
 * challenge runs as real pages on Monri's domain and returns to the configured
 * Success/Cancel URL — where /monri-callback/ finalizes the order. This sidesteps
 * the iframe entirely and can never stall.
 */
function installMonri3dsRedirectListener() {
	if (monri3dsListenerInstalled) return;
	monri3dsListenerInstalled = true;

	window.addEventListener('message', function (ev) {
		try {
			if (!ev.origin || ev.origin.indexOf('monri.com') === -1) return;

			let data = ev.data;
			if (typeof data === 'string') {
				if (data.indexOf('redirect_to') === -1 && data.indexOf('action') === -1) return;
				try { data = JSON.parse(data); } catch (e) { return; }
			}
			if (!data || typeof data !== 'object') return;

			let url = null;
			const args = Array.isArray(data.args) ? data.args : [];

			// Shape A: { method:'CLIENT_HANDLE_ACTION_REQUIRED', args:[{ payload:{ redirect_to, acs_url } }] }
			for (const a of args) {
				if (a && a.payload && (a.payload.redirect_to || a.payload.acs_url)) {
					url = a.payload.redirect_to || a.payload.acs_url;
					break;
				}
				// Shape B: { args:[{ result:{ actionRequired:{ redirect_to, acs_url } } }] }
				const ar = a && a.result && (a.result.actionRequired || a.result.action_required);
				if (ar && (ar.redirect_to || ar.acs_url)) {
					url = ar.redirect_to || ar.acs_url;
					break;
				}
			}
			// Shape C: top-level payload/action_required (defensive)
			if (!url && data.payload && (data.payload.redirect_to || data.payload.acs_url)) {
				url = data.payload.redirect_to || data.payload.acs_url;
			}

			if (!url || monri3dsRedirecting) return;

			// Only act on genuine Monri 3DS redirect URLs.
			if (!/monri\.com\/.*(three_ds|client_redirect|collect_browser_info)/i.test(url)) return;

			// Navigate to the exact redirect_to/acs_url Monri returned, unmodified.
			// (For 3DS2 this is the hosted collect_browser_info flow, which runs the
			// challenge full-page and returns to the configured Success/Cancel URL.)
			monri3dsRedirecting = true;
			// TEMP DIAGNOSTIC — remove before final sign-off.
			console.log('[BEPRO][monri] 3DS action_required → full-page redirect:', url);
			showOverlay('Preusmjeravamo na 3D Secure provjeru...');
			window.location.href = url;
		} catch (e) { /* never let the listener break checkout */ }
	}, false);
}

/**
 * Inicijalizacija checkout-a
 */
export default function initCheckout() {
	const form = document.querySelector('.js-checkout-form');
	const emptyMsg = document.querySelector('.js-checkout-empty');

	if (!form || !emptyMsg) return;

	// Provjeri da li ima stavki u korpi
	const items = Cart.items;
	if (!items || items.length === 0) {
		form.style.display = 'none';
		emptyMsg.style.display = 'flex';
		const mobile = document.querySelector('.js-mobile-summary');
		if (mobile) mobile.style.display = 'none';
		return;
	}

	form.style.display = 'flex';
	emptyMsg.style.display = 'none';

	renderOrderSummary(items);
	initMonriComponents();
	enableSubmitButton();
	bindFormSubmit(form);
	bindBackButton();
	bindMobileSummaryToggle();
}

/**
 * Renderuj pregled narudžbe (desktop + mobile summary)
 */
function renderOrderSummary(items) {
	const container = document.querySelector('.js-checkout-items');
	const mobileBody = document.querySelector('.js-mobile-summary-body');

	const config = window.beproCheckout || {};
	const currency = config.currency || 'KM';

	const itemsHtml = items.map(item => {
		const details = [item.size, item.color].filter(Boolean).map(escapeHtml).join(' / ');
		return `
			<div class="bepro-checkout__summary-item">
				<div class="bepro-checkout__summary-item-image">
					${item.image ? `<img src="${escapeHtml(item.image)}" alt="${escapeHtml(item.title)}">` : ''}
					<span class="bepro-checkout__summary-item-badge">${item.quantity}</span>
				</div>
				<div class="bepro-checkout__summary-item-info">
					<p class="bepro-checkout__summary-item-title">${escapeHtml(item.title)}</p>
					${details ? `<p class="bepro-checkout__summary-item-details">${details}</p>` : ''}
				</div>
				<div class="bepro-checkout__summary-item-total">
					${(item.price * item.quantity).toFixed(2)} ${escapeHtml(currency)}
				</div>
			</div>
		`;
	}).join('');

	if (container) container.innerHTML = itemsHtml;

	// Izračunaj totale (per-item shipping: totalQty * chunkCost; shipments = ceil(qty / chunkSize) — info only)
	const subtotal = items.reduce((sum, item) => sum + (item.price * item.quantity), 0);
	const totalQty = items.reduce((sum, item) => sum + item.quantity, 0);
	const chunkSize = config.shippingChunkSize || 3;
	const chunkCost = config.shippingChunkCost || 20;

	let shippingCost = 0;
	let shipments = 0;
	if (config.shippingEnabled && totalQty > 0) {
		if (config.freeAbove > 0 && subtotal >= config.freeAbove) {
			shippingCost = 0;
		} else {
			shipments = Math.ceil(totalQty / chunkSize);
			shippingCost = totalQty * chunkCost;
		}
	}

	const total = subtotal + shippingCost;

	const subtotalEl = document.querySelector('.js-checkout-subtotal');
	const shippingEl = document.querySelector('.js-checkout-shipping');
	const totalEl = document.querySelector('.js-checkout-total');
	const mobileTotalEl = document.querySelector('.js-mobile-total');

	if (subtotalEl) subtotalEl.textContent = `${subtotal.toFixed(2)} ${currency}`;
	if (shippingEl) shippingEl.textContent = shippingCost > 0 ? `${shippingCost.toFixed(2)} ${currency}` : 'Besplatna';
	if (totalEl) totalEl.textContent = `${total.toFixed(2)} ${currency}`;
	if (mobileTotalEl) mobileTotalEl.textContent = `${total.toFixed(2)} ${currency}`;

	// Multi-delivery notice (desktop + mobile)
	const multiNotices = document.querySelectorAll('.js-checkout-multidelivery');
	multiNotices.forEach((el) => {
		if (shipments > 1) {
			el.innerHTML = `
				<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round">
					<path d="M3 7h13v10H3zM16 10h4l1 3v4h-5"/>
					<circle cx="7" cy="19" r="2"/>
					<circle cx="18" cy="19" r="2"/>
				</svg>
				<span>Narudžba će biti poslana u <strong>${shipments} odvojene pošiljke</strong> (max ${chunkSize} artikla po pošiljci). Bićete obaviješteni o svakoj.</span>
			`;
			el.style.display = 'flex';
		} else {
			el.style.display = 'none';
		}
	});

	// Render mobile summary body (mirror of desktop summary)
	if (mobileBody) {
		const shippingRow = config.shippingEnabled
			? `<div class="bepro-checkout__summary-row"><span>Dostava</span><span>${shippingCost > 0 ? `${shippingCost.toFixed(2)} ${currency}` : 'Besplatna'}</span></div>`
			: '';
		mobileBody.innerHTML = `
			<div class="bepro-checkout__summary-items">${itemsHtml}</div>
			<div class="bepro-checkout__summary-totals">
				<div class="bepro-checkout__summary-row"><span>Iznos</span><span>${subtotal.toFixed(2)} ${currency}</span></div>
				${shippingRow}
			</div>
			<div class="bepro-checkout__summary-total"><span>Ukupno <small>(PDV uključen)</small></span><span>${total.toFixed(2)} ${currency}</span></div>
		`;
	}
}

/**
 * Toggle mobile summary expand/collapse
 */
function bindMobileSummaryToggle() {
	const btn = document.querySelector('.js-mobile-summary-toggle');
	const wrap = document.querySelector('.js-mobile-summary');
	if (!btn || !wrap) return;

	btn.addEventListener('click', (e) => {
		e.preventDefault();
		wrap.classList.toggle('bepro-checkout__mobile-summary--open');
		const isOpen = wrap.classList.contains('bepro-checkout__mobile-summary--open');
		const textEl = btn.querySelector('.bepro-checkout__mobile-toggle-text');
		if (textEl) textEl.textContent = isOpen ? 'Sakrij pregled narudžbe' : 'Prikaži pregled narudžbe';
	});
}

/**
 * Inicijaliziraj Monri Components (ako je konfigurisan)
 */
function initMonriComponents() {
	const config = window.beproCheckout || {};
	if (config.paymentProvider !== 'monri' || !config.monri) return;

	const { authenticityToken, componentsJsUrl } = config.monri;
	if (!authenticityToken) return;

	// Install the full-page 3DS redirect handler before any payment can start.
	installMonri3dsRedirectListener();

	// Dinamički učitaj Monri Components JS
	const script = document.createElement('script');
	script.src = componentsJsUrl;
	script.onload = () => {
		if (typeof Monri === 'undefined') {
			console.error('BEPRO: Monri.js nije učitan.');
			return;
		}

		// isThreeDSForm:true makes Monri Components run any 3DS2 challenge as a
		// FULL-PAGE navigation (handleFormThreeDsPayment) instead of inside a
		// fixed-position modal iframe (handleComponentsThreeDsPayment). The iframe
		// path stalls forever when the issuer's ACS refuses to be embedded
		// (X-Frame-Options), which is exactly what trapped customers on the
		// "Obrađujemo plaćanje..." overlay. Full-page redirect always completes:
		// Monri runs collect_browser_info + the ACS challenge as top-level pages,
		// then returns to the configured Success/Cancel URL where the server-side
		// /monri-callback/ finalizes the order.
		monriInstance = Monri(authenticityToken, { locale: 'bs', isThreeDSForm: true });
	};
	script.onerror = () => {
		console.error('BEPRO: Greška pri učitavanju Monri.js');
		showError('Greška pri učitavanju payment sistema. Osvježite stranicu.');
	};
	document.head.appendChild(script);
}

/**
 * Enable submit button nakon što se forma učita
 */
function enableSubmitButton() {
	const submitBtn = document.querySelector('.js-checkout-submit');
	if (submitBtn) submitBtn.disabled = false;
}

/**
 * Validacija forme
 */
function validateForm(form) {
	const fields = ['customer_name', 'customer_email', 'customer_phone', 'customer_address', 'customer_city', 'customer_zip'];
	let valid = true;

	// Očisti prethodne greške
	form.querySelectorAll('.js-field-error').forEach(el => {
		el.textContent = '';
		el.closest('.bepro-checkout__field')?.classList.remove('bepro-checkout__field--error');
	});

	fields.forEach(name => {
		const input = form.querySelector(`[name="${name}"]`);
		if (!input) return;

		const value = input.value.trim();
		const errorEl = form.querySelector(`.js-field-error[data-field="${name}"]`);

		if (!value) {
			valid = false;
			if (errorEl) {
				errorEl.textContent = 'Ovo polje je obavezno.';
				errorEl.closest('.bepro-checkout__field')?.classList.add('bepro-checkout__field--error');
			}
		}

		// Email validacija
		if (name === 'customer_email' && value && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) {
			valid = false;
			if (errorEl) {
				errorEl.textContent = 'Neispravan email.';
				errorEl.closest('.bepro-checkout__field')?.classList.add('bepro-checkout__field--error');
			}
		}
	});

	// Terms checkbox validacija
	const termsCheckbox = document.querySelector('.js-accept-terms');
	if (termsCheckbox && !termsCheckbox.checked) {
		valid = false;
		const label = document.querySelector('.js-terms-label');
		if (label) label.classList.add('bepro-checkout__consent--error');
		showToast('Morate prihvatiti uslove korištenja.', 'error', 4000);
	} else {
		const label = document.querySelector('.js-terms-label');
		if (label) label.classList.remove('bepro-checkout__consent--error');
	}

	return valid;
}

/**
 * Populate review block with form values for Shopify-style confirm
 */
function populateReviewBlock(form) {
	const email = form.querySelector('[name="customer_email"]')?.value.trim() || '';
	const name = form.querySelector('[name="customer_name"]')?.value.trim() || '';
	const address = form.querySelector('[name="customer_address"]')?.value.trim() || '';
	const city = form.querySelector('[name="customer_city"]')?.value.trim() || '';
	const zip = form.querySelector('[name="customer_zip"]')?.value.trim() || '';
	const phone = form.querySelector('[name="customer_phone"]')?.value.trim() || '';

	const emailEl = document.querySelector('.js-review-email');
	const addressEl = document.querySelector('.js-review-address');

	if (emailEl) emailEl.textContent = email;
	if (addressEl) {
		const parts = [name, `${address}, ${zip} ${city}`.replace(/^,\s*|\s*,\s*$/g, ''), phone].filter(Boolean);
		addressEl.textContent = parts.join(' · ');
	}
}

/**
 * Switch to payment step (step 2)
 */
function showPaymentStep(order) {
	const step1 = document.querySelector('.js-checkout-step-1');
	const step2 = document.querySelector('.js-checkout-step-2');
	const step1Indicator = document.querySelector('.js-step-1-indicator');
	const step2Indicator = document.querySelector('.js-step-2-indicator');
	const submitText = document.querySelector('.js-submit-text');
	const form = document.querySelector('.js-checkout-form');

	if (step1) step1.style.display = 'none';
	if (step2) step2.style.display = 'block';

	// Populate review block with submitted values
	if (form) populateReviewBlock(form);

	// Update step indicators
	if (step1Indicator) {
		step1Indicator.classList.remove('bepro-checkout__step--active');
		step1Indicator.classList.add('bepro-checkout__step--done');
	}
	if (step2Indicator) {
		step2Indicator.classList.add('bepro-checkout__step--active');
	}

	// Update button text
	if (submitText) {
		submitText.textContent = `Plati ${order.total.toFixed(2)} ${order.currency}`;
	}

	// Hide any previous errors
	const errorDiv = document.querySelector(".js-checkout-error");
	if (errorDiv) { errorDiv.style.display = "none"; errorDiv.textContent = ""; }

	isPaymentStep = true;

	// Scroll to payment section
	if (step2) {
		step2.scrollIntoView({ behavior: 'smooth', block: 'start' });
	}
}

/**
 * Switch back to step 1
 */
function showDeliveryStep() {
	const step1 = document.querySelector('.js-checkout-step-1');
	const step2 = document.querySelector('.js-checkout-step-2');
	const step1Indicator = document.querySelector('.js-step-1-indicator');
	const step2Indicator = document.querySelector('.js-step-2-indicator');
	const submitText = document.querySelector('.js-submit-text');

	if (step1) step1.style.display = 'block';
	if (step2) step2.style.display = 'none';

	// Update step indicators
	if (step1Indicator) {
		step1Indicator.classList.remove('bepro-checkout__step--done');
		step1Indicator.classList.add('bepro-checkout__step--active');
	}
	if (step2Indicator) {
		step2Indicator.classList.remove('bepro-checkout__step--active');
	}

	// Update button text
	if (submitText) {
		submitText.textContent = 'Nastavi na plaćanje';
	}

	isPaymentStep = false;
	hideError();
}

/**
 * Bind back button
 */
function bindBackButton() {
	const backBtns = document.querySelectorAll('.js-checkout-back');
	backBtns.forEach((btn) => {
		btn.addEventListener('click', (e) => {
			e.preventDefault();
			showDeliveryStep();
		});
	});
}

/**
 * Bind form submit
 */
function bindFormSubmit(form) {
	const handler = async (e) => {
		e.preventDefault();

		const config = window.beproCheckout || {};
		const submitBtn = form.querySelector('.js-checkout-submit');
		const submitText = form.querySelector('.js-submit-text');
		const spinner = form.querySelector('.js-submit-spinner');

		// If we're on step 2 (payment), process card payment
		if (isPaymentStep && currentOrder) {
			setLoading(true, submitBtn, submitText, spinner);
			hideError();
			showOverlay('Obrađujemo plaćanje...');

			try {
				await confirmMonriPayment(form, currentOrder);
			} catch (error) {
				hideOverlay();
				showError(error.message);
				setLoading(false, submitBtn, submitText, spinner);
			}
			return;
		}

		// Step 1: Validate form
		if (!validateForm(form)) return;

		setLoading(true, submitBtn, submitText, spinner);
		hideError();

		try {
			// Create order
			const orderResult = await createOrder(form);

			if (!orderResult.success) {
				throw new Error(orderResult.data?.message || 'Greška pri kreiranju narudžbe.');
			}

			currentOrder = orderResult.data;

			if (config.paymentProvider === 'monri' && config.monri) {
				// Create payment session and mount card
				hideError();
				// Show step 2 first so card element is visible for mount
				showPaymentStep(currentOrder);
				showToast("Unesite podatke kartice za završetak plaćanja.", "info", 4000);
				await setupMonriPayment(form, currentOrder);
				setLoading(false, submitBtn, submitText, spinner);
			} else {
				// No online payment — redirect to confirmation
				showOverlay('Preusmjeravamo vas na potvrdu...');
				Cart.clear();
				window.location.href = config.confirmationUrl + '?order=' + currentOrder.order_number + '&key=' + currentOrder.order_key;
			}

		} catch (error) {
			showError(error.message);
			setLoading(false, submitBtn, submitText, spinner);
		}
	};

	form._checkoutHandler = handler;
	form.addEventListener('submit', handler);
}

/**
 * Refresh the checkout nonce from server (for stale pages)
 */
async function refreshNonce() {
	const config = window.beproCheckout || {};
	const fd = new FormData();
	fd.append('action', 'bepro_refresh_nonce');
	try {
		const res = await fetch(config.ajaxUrl, { method: 'POST', body: fd });
		const json = await res.json();
		if (json.success && json.data && json.data.nonce) {
			config.nonce = json.data.nonce;
			return json.data.nonce;
		}
	} catch (e) { /* ignore */ }
	return null;
}

/**
 * Kreiraj narudžbu via AJAX (with nonce auto-refresh on stale nonce)
 */
async function createOrder(form) {
	const config = window.beproCheckout || {};

	const doRequest = () => {
		const formData = new FormData(form);
		formData.append('action', 'bepro_create_order');
		formData.append('nonce', config.nonce);
		formData.append('items', JSON.stringify(Cart.items));
		return fetch(config.ajaxUrl, { method: 'POST', body: formData }).then(res => res.json());
	};

	let result = await doRequest();

	// If nonce is stale (page open too long), refresh and retry once
	if (!result.success && result.data && result.data.code === 'nonce_failed') {
		const fresh = await refreshNonce();
		if (fresh) {
			result = await doRequest();
		}
	}

	return result;
}

/**
 * Setup Monri payment: get client_secret and mount card
 */
async function setupMonriPayment(form, order) {
	const config = window.beproCheckout || {};

	if (!monriInstance) {
		throw new Error('Payment sistem nije spreman. Osvježite stranicu.');
	}

	// Get client_secret from server (with nonce auto-refresh on stale nonce)
	const doPaymentRequest = () => {
		const fd = new FormData();
		fd.append('action', 'bepro_create_payment');
		fd.append('nonce', config.nonce);
		fd.append('order_id', order.order_id);
		fd.append('order_number', order.order_number);
		return fetch(config.ajaxUrl, { method: 'POST', body: fd }).then(res => res.json());
	};

	let paymentResult = await doPaymentRequest();

	if (!paymentResult.success && paymentResult.data?.code === 'nonce_failed') {
		const fresh = await refreshNonce();
		if (fresh) paymentResult = await doPaymentRequest();
	}

	if (!paymentResult.success) {
		throw new Error(paymentResult.data?.message || 'Greška pri kreiranju plaćanja.');
	}

	const clientSecret = paymentResult.data.client_secret;

	// Create Monri Components with client_secret. isThreeDSForm is repeated here
	// (and on the card element below) because the SDK reads the flag from the
	// component/controller config — not only from the Monri() init options — when
	// deciding between the stalling iframe modal and the full-page form 3DS.
	const components = monriInstance.components({
		clientSecret: clientSecret,
		isThreeDSForm: true
	});

	const cardElement = document.getElementById('monri-card-element');

	// Clear any existing content
	if (cardElement) cardElement.innerHTML = '';

	const card = components.create('card', {
		isThreeDSForm: true,
		style: {
			base: {
				fontSize: '16px',
				color: '#1a1a1a',
				fontFamily: 'inherit',
				'::placeholder': {
					color: '#9ca3af'
				}
			},
			invalid: {
				color: '#dc2626'
			}
		}
	});

	card.mount('monri-card-element');
	monriCard = card;

	// Listener za greške
	card.addChangeListener(function (event) {
		const errorDiv = document.getElementById('monri-card-errors');
		if (errorDiv) {
			errorDiv.textContent = event.error ? event.error.message : '';
		}
	});
}

/**
 * Confirm Monri payment (step 2 submit)
 */
async function confirmMonriPayment(form, order) {
	const config = window.beproCheckout || {};

	if (!monriInstance || !monriCard) {
		throw new Error('Payment sistem nije spreman. Osvježite stranicu.');
	}

	const transactionParams = {
		address: form.querySelector('[name="customer_address"]').value,
		fullName: form.querySelector('[name="customer_name"]').value,
		city: form.querySelector('[name="customer_city"]').value,
		zip: form.querySelector('[name="customer_zip"]').value,
		phone: form.querySelector('[name="customer_phone"]').value,
		country: 'BA',
		email: form.querySelector('[name="customer_email"]').value,
	};

	// With isThreeDSForm:true (set at init), Monri runs any 3DS2 CHALLENGE as a
	// full-page navigation, so confirmPayment never resolves on the challenge path
	// — the browser leaves this page for Monri's 3DS and returns to the configured
	// Success/Cancel URL. confirmPayment ONLY resolves here for the frictionless
	// (no-challenge) path, or for an immediate decline/error.
	//
	// Watchdog: a last-resort UX guard so the blocking overlay can never spin
	// forever if, for any reason, neither the full-page redirect nor a resolution
	// happens (e.g. SDK/network anomaly). It is NOT the completion mechanism.
	let settled = false;
	const watchdog = setTimeout(() => {
		if (settled) return;
		hideOverlay();
		showError('Provjera plaćanja traje duže nego obično. Ako ste dovršili 3D Secure provjeru u banci, narudžba će biti potvrđena i dobit ćete email s potvrdom. U suprotnom osvježite stranicu i pokušajte ponovo.');
	}, 180000);

	let result;
	try {
		result = await monriInstance.confirmPayment(monriCard, transactionParams);
	} finally {
		settled = true;
		clearTimeout(watchdog);
	}

	// TEMP DIAGNOSTIC — capture exact confirmPayment shape during go-live testing.
	// Remove/gate before final sign-off.
	try { console.log('[BEPRO][monri] confirmPayment result:', JSON.stringify(result)); } catch (e) { console.log('[BEPRO][monri] confirmPayment result (raw):', result); }

	if (result && result.error) {
		throw new Error(result.error.message || 'Greška pri plaćanju.');
	}

	// Normalise across every plausible response shape Monri Components / the raw
	// confirm response may hand back. action_required can sit at the top level, on
	// .result, on .payment_result, or as camelCase actionRequired; redirect target
	// may be redirect_to or acs_url.
	const r = (result && result.result) || result || {};
	const action =
		r.action_required || r.actionRequired ||
		(r.payment_result && (r.payment_result.action_required || r.payment_result.actionRequired)) ||
		(result && (result.action_required || result.actionRequired)) ||
		null;
	const redirectUrl = action && (action.redirect_to || action.acs_url);

	const status = r.status || (r.payment_result && r.payment_result.status) || '';

	// Approved — payment (incl. any frictionless 3DS2 step) completed successfully.
	if (status === 'approved' && !redirectUrl) {
		showOverlay('Plaćanje uspješno! Preusmjeravamo...');
		showToast('Plaćanje uspješno!', 'success', 3000);
		Cart.clear();
		window.location.href = config.confirmationUrl + '?order=' + order.order_number + '&key=' + order.order_key;
		return;
	}

	// Defensive 3DS2 redirect: if the SDK ever returns the action to us instead of
	// navigating itself, drive the TOP window full-page to Monri's 3DS URL. Monri
	// then returns to the Success/Cancel URL and the callback finalizes the order.
	if (redirectUrl) {
		showOverlay('Preusmjeravamo na 3D Secure provjeru...');
		window.location.href = redirectUrl;
		return;
	}

	// Declined / failed — surface the reason and let the user retry.
	const msg = r.response_message || (r.payment_result && r.payment_result.response_message);
	throw new Error(msg || 'Plaćanje nije uspjelo. Pokušajte ponovo ili koristite drugu karticu.');
}

/**
 * Overlay helpers
 */
function showOverlay(text) {
	const overlay = document.querySelector('.js-checkout-overlay');
	if (!overlay) return;
	const textEl = overlay.querySelector('.js-overlay-text');
	if (textEl && text) textEl.textContent = text;
	overlay.style.display = 'flex';
	document.body.style.overflow = 'hidden';
}

function hideOverlay() {
	const overlay = document.querySelector('.js-checkout-overlay');
	if (!overlay) return;
	overlay.style.display = 'none';
	document.body.style.overflow = '';
}

/**
 * UI helpers
 */
function setLoading(loading, btn, text, spinner) {
	if (btn) btn.disabled = loading;
	if (spinner) spinner.style.display = loading ? 'inline-flex' : 'none';
	if (text && loading) text.style.opacity = '0.5';
	if (text && !loading) text.style.opacity = '1';
}

function showError(message) {
	if (message) showToast(message, "error", 5000);
	const errorDiv = document.querySelector('.js-checkout-error');
	if (errorDiv) {
		errorDiv.textContent = message;
		errorDiv.style.display = 'block';
	}
}

function hideError() {
	const errorDiv = document.querySelector('.js-checkout-error');
	if (errorDiv) {
		errorDiv.style.display = 'none';
		errorDiv.textContent = '';
	}
}

// ─── Legal page modal ─────────────────────────────────────────────────────────

(function initLegalModal() {
	const overlay  = document.querySelector('.js-legal-modal-overlay');
	const body     = document.querySelector('.js-legal-modal-body');
	const titleEl  = document.querySelector('.bepro-legal-modal__title');
	const loader   = document.querySelector('.js-legal-modal-loader');
	if (!overlay || !body) return;

	// Cache fetched pages to avoid repeat requests
	const pageCache = {};

	function openModal(url, title) {
		titleEl.textContent = title || '';
		overlay.style.display = 'flex';
		document.body.style.overflow = 'hidden';

		// Show loader, hide any previous content
		loader.style.display = 'flex';
		const prev = body.querySelector('.bepro-legal-modal__content');
		if (prev) prev.remove();

		if (pageCache[url]) {
			renderContent(pageCache[url]);
			return;
		}

		fetch(url)
			.then(r => r.text())
			.then(html => {
				const doc = new DOMParser().parseFromString(html, 'text/html');
				const content =
					doc.querySelector('.basic-content__body') ||
					doc.querySelector('.entry-content') ||
					doc.querySelector('main');
				const text = content ? content.innerHTML : '<p>Sadržaj nije dostupan.</p>';
				pageCache[url] = text;
				renderContent(text);
			})
			.catch(() => renderContent('<p>Greška pri učitavanju sadržaja. Pokušajte ponovo.</p>'));
	}

	function renderContent(html) {
		loader.style.display = 'none';
		const div = document.createElement('div');
		div.className = 'bepro-legal-modal__content';
		div.innerHTML = html;
		body.appendChild(div);
	}

	function closeModal() {
		overlay.style.display = 'none';
		document.body.style.overflow = '';
	}

	// Clicks on legal links
	document.addEventListener('click', e => {
		const link = e.target.closest('.js-legal-modal');
		if (!link) return;
		e.preventDefault();
		openModal(link.getAttribute('href'), link.getAttribute('data-modal-title'));
	});

	// Close on backdrop / close button
	document.addEventListener('click', e => {
		if (e.target.closest('.js-legal-modal-close')) closeModal();
	});

	// Close on Escape
	document.addEventListener('keydown', e => {
		if (e.key === 'Escape' && overlay.style.display !== 'none') closeModal();
	});
})();

// ─── Trust badge info popovers ────────────────────────────────────────────────

(function initTrustPopovers() {
	function closeAll(except) {
		document.querySelectorAll('.bepro-checkout__trust-popover.is-open').forEach(pop => {
			if (pop === except) return;
			pop.classList.remove('is-open');
			const btn = pop.parentElement?.querySelector('.js-trust-info');
			if (btn) btn.setAttribute('aria-expanded', 'false');
		});
	}

	document.addEventListener('click', e => {
		const btn = e.target.closest('.js-trust-info');
		if (btn) {
			e.preventDefault();
			e.stopPropagation();
			const popover = btn.parentElement?.querySelector('.bepro-checkout__trust-popover');
			if (!popover) return;
			const isOpen = popover.classList.contains('is-open');
			closeAll(isOpen ? null : popover);
			popover.classList.toggle('is-open', !isOpen);
			btn.setAttribute('aria-expanded', String(!isOpen));
			return;
		}
		// Click outside any popover → close all
		if (!e.target.closest('.bepro-checkout__trust-popover')) {
			closeAll();
		}
	});

	document.addEventListener('keydown', e => {
		if (e.key === 'Escape') closeAll();
	});
})();
